import { Injectable, Inject, Optional } from "@angular/core";
import { API_BASE_URL } from "./service-proxies";
import {
  mergeMap as _observableMergeMap,
  catchError as _observableCatch,
} from "rxjs/operators";
import { Observable,
  throwError as _observableThrow,
  of as _observableOf, } from "rxjs";
import { HttpClient,
  HttpHeaders,
  HttpResponse,
  HttpResponseBase, } from "@angular/common/http";
import { DocumentFileModel } from "./documents-service-proxies";


@Injectable()
export class TodoListServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  /**
   * @param body (optional)
   * @return Success
   */
  create(body: CreateTodoListDto | undefined): Observable<TodoListDto> {
    let url_ = this.baseUrl + "/api/services/app/TodoList/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreate(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<TodoListDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<TodoListDto>;
        })
      );
  }

  protected processCreate(
    response: HttpResponseBase
  ): Observable<TodoListDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TodoListDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  Complete(id: number): Observable<TodoListDto> {
    let url_ = this.baseUrl + "/api/services/app/TodoList/SetAsCompleted";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify({
      id: id
    });

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processComplete(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processComplete(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<TodoListDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<TodoListDto>;
        })
      );
  }

  protected processComplete(
    response: HttpResponseBase
  ): Observable<TodoListDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TodoListDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  delete(id: number | undefined): Observable<void> {
    let url_ = this.baseUrl + "/api/services/app/TodoList/Delete?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    return this.http
      .request("delete", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDelete(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDelete(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processDelete(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }


  /**
   * @param id (optional)
   * @return Success
   */
  get(id: number | undefined): Observable<TodoListDto> {
    let url_ = this.baseUrl + "/api/services/app/TodoList/Get?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGet(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGet(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<TodoListDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<TodoListDto>;
        })
      );
  }

  protected processGet(
    response: HttpResponseBase
  ): Observable<TodoListDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TodoListDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param keyword (optional)
   * @param isActive (optional)
   * @param skipCount (optional)
   * @param maxResultCount (optional)
   * @return Success
   */
  getAll(
    keyword: string | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined,
    caseId: number|null,
    caseRequestId: number|null
  ): Observable<TodoListDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/TodoList/GetAll?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";

      if (caseId != null)
        url_ += "CaseId=" + encodeURIComponent("" + caseId) + "&";
      if (caseRequestId != null)
        url_ += "CaseId=" + encodeURIComponent("" + caseRequestId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<TodoListDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<TodoListDtoPagedResultDto>;
        })
      );
  }

  getAllIncoming(
    keyword: string | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined
  ): Observable<TodoListDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/TodoList/GetAllIncoming?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<TodoListDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<TodoListDtoPagedResultDto>;
        })
      );
  }

  protected processGetAll(
    response: HttpResponseBase
  ): Observable<TodoListDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TodoListDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  getFile(
    fileId: number | undefined,
    todoListId: number | undefined
  ): Observable<DocumentFileModel> {
    let url_ = this.baseUrl + "/api/services/app/TodoList/GetTodoListFile?";
    if (fileId === null)
      throw new Error("The parameter 'FileId' cannot be null.");
    else if (fileId !== undefined)
      url_ += "FileId=" + encodeURIComponent("" + fileId) + "&";

    if (todoListId === null)
      throw new Error("The parameter 'TodoListId' cannot be null.");
    else if (todoListId !== undefined)
      url_ += "TodoListId=" + encodeURIComponent("" + todoListId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetTodoListFileFile(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetTodoListFileFile(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<DocumentFileModel>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<DocumentFileModel>;
        })
      );
  }

  protected processGetTodoListFileFile(
    response: HttpResponseBase
  ): Observable<DocumentFileModel> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DocumentFileModel.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return Success
   */
  update(body: CreateTodoListDto | undefined): Observable<TodoListDto> {
    let url_ = this.baseUrl + "/api/services/app/TodoList/Update";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("put", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdate(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<TodoListDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<TodoListDto>;
        })
      );
  }

  protected processUpdate(
    response: HttpResponseBase
  ): Observable<TodoListDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TodoListDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }
}

export class TodoListDto implements ITodoListDto {
    id: number;
    title: string;
    description: string;
    caseId: number;
    caseRequestId: number;
    deadline: string;
    responsibleUserFullName: string;
    creatorUserFullName: string;
    completed: boolean;
    files: DocumentFileModel[];
    fileIds: number[];

  constructor(data?: ITodoListDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.title = _data["title"];
      this.description = _data["description"];
      this.caseId = _data["caseId"];
      this.caseRequestId = _data["caseRequestId"];
      this.deadline = _data["deadline"];
      this.responsibleUserFullName = _data["responsibleUserFullName"];
      this.creatorUserFullName = _data["creatorUserFullName"];
      this.completed = _data["completed"];
      if (Array.isArray(_data["files"])) {
        this.files = [] as any;
        for (let item of _data["files"]) this.files.push(item);
      }
      if (Array.isArray(_data["fileIds"])) {
        this.fileIds = [] as any;
        for (let item of _data["fileIds"]) this.fileIds.push(item);
      }
    }
  }

  static fromJS(data: any): TodoListDto {
    data = typeof data === "object" ? data : {};
    let result = new TodoListDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["title"] = this.title;
    data["description"] = this.description;
    data["caseId"] = this.caseId;
    data["caseRequestId"] = this.caseRequestId;
    data["deadline"] = this.deadline;
    data["responsibleUserFullName"] = this.responsibleUserFullName;
    data["creatorUserFullName"] = this.creatorUserFullName;
    data["completed"] = this.completed;
    if (Array.isArray(this.files)) {
        data["files"] = [];
        for (let item of this.files) data["files"].push(item);
      }
    if (Array.isArray(this.fileIds)) {
        data["fileIds"] = [];
        for (let item of this.fileIds) data["fileIds"].push(item);
      }
    return data;
  }

  clone(): TodoListDto {
    const json = this.toJSON();
    let result = new TodoListDto();
    result.init(json);
    return result;
  }
}

export interface ITodoListDto {
    id: number;
    title: string;
    description: string;
    caseId: number;
    caseRequestId: number;
    deadline: string;
    responsibleUserFullName: string;
    creatorUserFullName: string;
    completed: boolean;
    files: DocumentFileModel[];
    fileIds: number[] ;
}

export class CreateTodoListDto implements ICreateTodoListDto {
  id: number;
  title: string;
  description: string;
  caseId: number;
  caseRequestId: number;
  deadline: string;
  files: DocumentFileModel[] = [];
  fileIds: number[] = [];

  constructor(data?: ICreateTodoListDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.title = _data["title"];
      this.description = _data["description"];
      this.caseId = _data["caseId"];
      this.caseRequestId = _data["caseRequestId"];
      this.deadline = _data["deadline"];
      if (Array.isArray(_data["files"])) {
        this.files = [] as any;
        for (let item of _data["files"]) this.files.push(item);
      }
      if (Array.isArray(_data["fileIds"])) {
        this.fileIds = [] as any;
        for (let item of _data["fileIds"]) this.fileIds.push(item);
      }
    }
  }

  static fromJS(data: any): CreateTodoListDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateTodoListDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["title"] = this.title;
    data["description"] = this.description;
    data["caseId"] = this.caseId;
    data["caseRequestId"] = this.caseRequestId;
    data["deadline"] = this.deadline;
    if (Array.isArray(this.files)) {
        data["files"] = [];
        for (let item of this.files) data["files"].push(item);
      }
    if (Array.isArray(this.fileIds)) {
        data["fileIds"] = [];
        for (let item of this.fileIds) data["fileIds"].push(item);
      }
    return data;
  }

  clone(): CreateTodoListDto {
    const json = this.toJSON();
    let result = new CreateTodoListDto();
    result.init(json);
    return result;
  }
}

export interface ICreateTodoListDto {
    id: number;
    title: string;
    description: string;
    caseId: number;
    caseRequestId: number;
    deadline: string;
    files: DocumentFileModel[];
    fileIds: number[];
}

export class TodoListDtoPagedResultDto
  implements ITodoListDtoPagedResultDto
{
  items: TodoListDto[] | undefined;
  totalCount: number;

  constructor(data?: ITodoListDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(TodoListDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): TodoListDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new TodoListDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): TodoListDtoPagedResultDto {
    const json = this.toJSON();
    let result = new TodoListDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface ITodoListDtoPagedResultDto {
  items: TodoListDto[] | undefined;
  totalCount: number;
}

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next("");
      observer.complete();
    } else {
      let reader = new FileReader();
      reader.onload = (event) => {
        observer.next((event.target as any).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}

export class ApiException extends Error {
  message: string;
  status: number;
  response: string;
  headers: { [key: string]: any };
  result: any;

  constructor(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result: any
  ) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiException {
    return obj.isApiException === true;
  }
}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any
): Observable<any> {
  if (result !== null && result !== undefined) return _observableThrow(result);
  else
    return _observableThrow(
      new ApiException(message, status, response, headers, null)
    );
}
